import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, fetchFileUrl, uploadFile } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { Button, ErrorText, Spinner, Textarea, cls, fmtDateTime } from '../../lib/ui.tsx';

interface Message {
  id: string;
  senderId: string;
  body: string;
  attachmentFileId: string | null;
  exerciseId: string | null;
  takeId: string | null;
  createdAt: string;
  sender: { id: string; name: string; role: string };
}

/** F4 · Attachment renderer — image preview if it decodes, download link otherwise. */
function Attachment({ fileId }: { fileId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(true);
  useEffect(() => {
    let alive = true;
    void fetchFileUrl(fileId)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
    };
  }, [fileId]);
  if (!url) return null;
  return isImage ? (
    <img src={url} alt="Attachment" onError={() => setIsImage(false)} className="mt-2 max-h-48 rounded-lg" />
  ) : (
    <a href={url} download className="mt-2 block text-sm underline">
      📎 Attachment
    </a>
  );
}

function Bubble({ msg, own }: { msg: Message; own: boolean }) {
  return (
    <div className={cls('flex', own ? 'justify-end' : 'justify-start')}>
      <div
        className={cls(
          'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
          own ? 'bg-brand-600 text-white' : 'border border-slate-200 bg-white text-slate-800',
        )}
      >
        <p className="whitespace-pre-wrap break-words">{msg.body}</p>
        {msg.attachmentFileId && <Attachment fileId={msg.attachmentFileId} />}
        {msg.takeId && (
          <Link
            to={`/app/takes/${msg.takeId}`}
            className={cls('mt-1 block text-xs underline', own ? 'text-brand-100' : 'text-brand-700')}
          >
            🎙️ take
          </Link>
        )}
        <p className={cls('mt-1 text-[10px]', own ? 'text-brand-200' : 'text-slate-400')}>{fmtDateTime(msg.createdAt)}</p>
      </div>
    </div>
  );
}

/** F1 · Single thread with the student's own coach. */
export default function MessagesPage() {
  const { me, loading } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState<{ id: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const scrollBox = useRef<HTMLDivElement>(null);

  const { data: messages, isLoading } = useQuery({
    queryKey: ['msg-thread', me?.id],
    queryFn: () => api<Message[]>(`/api/messages/thread/${me?.id}`),
    enabled: !!me,
    refetchInterval: 10_000,
  });

  useEffect(() => {
    const el = scrollBox.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages?.length]);

  const send = useMutation({
    mutationFn: () =>
      api<Message>('/api/messages', { body: { body, attachmentFileId: attachment?.id ?? null } }),
    onSuccess: () => {
      setBody('');
      setAttachment(null);
      void qc.invalidateQueries({ queryKey: ['msg-thread', me?.id] });
    },
  });

  async function onPickFile(file: File) {
    setUploading(true);
    setUploadError(null);
    try {
      const up = await uploadFile(file, file.name);
      setAttachment({ id: up.id, name: file.name });
    } catch (err) {
      setUploadError(err);
    } finally {
      setUploading(false);
    }
  }

  if (loading || !me) return <Spinner />;

  return (
    <div className="space-y-3">
      <h1 className="text-lg font-bold">Messages</h1>
      <div className="flex h-[70vh] flex-col rounded-xl border border-slate-200 bg-slate-50">
        <div ref={scrollBox} className="flex-1 space-y-2 overflow-y-auto p-3">
          {isLoading ? (
            <Spinner />
          ) : !messages || messages.length === 0 ? (
            <p className="p-6 text-center text-xs text-slate-400">No messages yet — write your coach below 👇</p>
          ) : (
            messages.map((m) => <Bubble key={m.id} msg={m} own={m.senderId === me.id} />)
          )}
        </div>
        <div className="border-t border-slate-200 bg-white p-3">
          {attachment && (
            <p className="mb-2 flex items-center gap-2 text-xs text-slate-600">
              📎 {attachment.name}
              <button className="text-red-600 underline" onClick={() => setAttachment(null)}>
                remove
              </button>
            </p>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickFile(f);
                e.target.value = '';
              }}
            />
            <Button variant="secondary" onClick={() => fileInput.current?.click()} disabled={uploading} aria-label="Attach file">
              {uploading ? '…' : '📎'}
            </Button>
            <Textarea rows={2} placeholder="Write a message…" value={body} onChange={(e) => setBody(e.target.value)} />
            <Button onClick={() => send.mutate()} disabled={send.isPending || body.trim().length === 0}>
              Send
            </Button>
          </div>
          <ErrorText error={send.error ?? uploadError} />
        </div>
      </div>
    </div>
  );
}
